import React from 'react';
import PropTypes from 'prop-types';

import { css, StyleSheet } from 'aphrodite';
import { Link } from 'react-router-dom';

import {
  FG_1,
  FG_2,
  FG_3,
  FONT_BODY,
  FONT_SIZE_SMALL,
  FONT_WEIGHT_BOLD,
  FONT_WEIGHT_MEDIUM,
  FONT_WEIGHT_SEMIBOLD,
  HOVER_GREY,
  PRIMARY_HOVER,
  PRIMARY_LIGHT,
  SPACE_2,
  SPACE_4,
  STROKE_GREY,
} from 'components/upb_viewer_beta/css/betaTokens';

/**
 * BetaBreadcrumb — breadcrumb nav bar.
 *
 * Props:
 *   crumbs      Array<{ label: string, url?: string, tag?: string }>
 *               The last item is the current page (no link, bold).
 *               `tag` (e.g. 'L1', 'L2') renders an inline pill after the label.
 *   onNavigate  Optional(url) => void, invoked instead of the default Link
 *               navigation on a plain click. Lets a parent that owns search
 *               state (e.g. the beta viewer container) reset it on crumb clicks.
 */
function BetaBreadcrumb({ crumbs, onNavigate }) {
  if (!crumbs || crumbs.length === 0) return null;

  // A bare history push (react-router's default Link behavior) would leave
  // an active search query in place, since nothing tells the parent view to
  // clear it. Routing plain clicks through onNavigate lets the parent reset
  // search state before changing the page, same as every other nav path.
  // Modified clicks (new tab/window) still fall through to the default
  // anchor behavior, mirroring react-router's own Link.
  const handleClick = (e, url) => {
    if (!onNavigate || e.defaultPrevented || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) {
      return;
    }
    e.preventDefault();
    onNavigate(url);
  };

  return (
    <nav className={css(styles.bar)} aria-label="breadcrumb">
      {crumbs.map((crumb, idx) => {
        const isLast = idx === crumbs.length - 1;
        const content = (
          <>
            {crumb.label}
            {crumb.tag && (
              <span className={css(styles.tag, isLast && styles.tagCurrent)}>{crumb.tag}</span>
            )}
          </>
        );
        return (
          // eslint-disable-next-line react/no-array-index-key
          <React.Fragment key={idx}>
            {idx > 0 && <span className={css(styles.separator)}>›</span>}
            {isLast || !crumb.url ? (
              <span className={css(styles.current)}>{content}</span>
            ) : (
              <Link
                to={crumb.url}
                className={css(styles.link)}
                onClick={e => handleClick(e, crumb.url)}
              >
                {content}
              </Link>
            )}
          </React.Fragment>
        );
      })}
    </nav>
  );
}

const styles = StyleSheet.create({
  bar: {
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: SPACE_2,
    padding: `${SPACE_2}px 0 ${SPACE_4}px`,
    fontFamily: FONT_BODY,
    fontWeight: FONT_WEIGHT_SEMIBOLD,
    fontSize: FONT_SIZE_SMALL,
    color: FG_2,
  },

  link: {
    color: FG_2,
    textDecoration: 'none',
    ':hover': {
      color: PRIMARY_HOVER,
      textDecoration: 'underline',
    },
  },

  separator: {
    color: STROKE_GREY,
    fontWeight: FONT_WEIGHT_MEDIUM,
    fontSize: FONT_SIZE_SMALL,
    userSelect: 'none',
    margin: `0 ${SPACE_2}px`,
  },

  current: {
    color: FG_1,
    fontWeight: FONT_WEIGHT_BOLD,
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
  },

  tag: {
    fontFamily: FONT_BODY,
    fontWeight: FONT_WEIGHT_BOLD,
    fontSize: 9,
    color: FG_3,
    backgroundColor: HOVER_GREY,
    padding: '2px 7px',
    borderRadius: 9999,
    letterSpacing: '0.04em',
    marginLeft: 4,
    verticalAlign: '1px',
  },

  tagCurrent: {
    backgroundColor: PRIMARY_LIGHT,
    color: PRIMARY_HOVER,
  },
});

BetaBreadcrumb.propTypes = {
  crumbs: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string.isRequired,
      url: PropTypes.string,
      tag: PropTypes.string,
    }),
  ).isRequired,
  onNavigate: PropTypes.func,
};

BetaBreadcrumb.defaultProps = {
  onNavigate: null,
};

export default BetaBreadcrumb;
